<template>
    <form class="game-form">
        <div class="columns">
            <div class="column">
                <div class="field is-horizontal">
                    <div class="field-label is-normal">
                        <label class="label"></label>
                    </div>
                    <div class="field-body">
                        <div class="field">
                            <h3 class="title is-3" v-if="!isEdit">Create a new challenge</h3>
                            <h3 class="title is-3" v-if="isEdit">Edit this challenge</h3>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>
        <div class="columns">
            <div class="column">
                <div class="field is-horizontal">
                    <div class="field-label is-normal">
                        <label class="label">Challenge name:</label>
                    </div>
                    <div class="field-body">
                        <div class="field">
                            <div class="control">
                                <b-input placeholder="Example: Sydney history Darling Harbour" type="text" v-model="name"></b-input>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Question text:</label>
                </div>
                <div class="field-body is-fullwidth">
          <textarea
                  :value="question"
                  @input="updateMarkdownPreview"
                  class="textarea"
                  placeholder="Enter your question here in less than 200 characters"
                  rows="6"
          ></textarea>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Category:</label>
                </div>
                <div class="field-body">
                    <div class="field">
                        <div class="control">
                            <b-input placeholder="Examples: Fun, Sydney, Reusable, etc" type="text" v-model="category" maxlength="20"></b-input>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Challenge picture:</label>
                </div>
                <div class="field-body">
                    <div class="field">
                        <div class="control">
                            <div style="position: relative">
                                <picture-input
                                        id="pictureInput"
                                        ref="challengePicture"
                                        @change="onPictureSelected"
                                        width="250"
                                        height="150"
                                        buttonClass="button is-primary is-outlined"
                                        removeButtonClass="button is-outlined"
                                        :customStrings="fileUploadCustomTexts"
                                        :removable="true"
                                />
                                <div v-if="imagePreviewUrl" id="injectedPreview" style="background-color: white">
                                    <img id="injectedPreviewImage" v-bind:src="imagePreviewUrl"/>
                                </div>
                                <button v-if="imagePreviewUrl" @click.prevent="hideImagePreview"
                                        class="button is-info is-outlined"
                                        style="width: 250px; margin-top: 5px;">Change image
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Activity type:</label>
                </div>
                <b-select v-model="activityType" placeholder="Select the activity type">
                    <option v-for="activity in activityTypes" :value="activity.label">
                        {{activity.label}}
                    </option>
                </b-select>
            </div>

        </div>
        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label"> </label>
                </div>
                <div class="field-body">
                    <div class="control">
                        <b-message type="is-success">{{ typeDescription }}</b-message>
                    </div>
                </div>
            </div>
        </div>
        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Answers type:</label>
                </div>
                <div class="field-body">
                    <b-field>
                        <b-radio v-for="questionType of questionTypes" :key="questionType.code" v-model="type" :native-value="questionType.code" type="is-link">
                            <div class="column field is-horizontal">
                                {{ questionType.label }}
                            </div>
                        </b-radio>
                    </b-field>
                </div>
            </div>
        </div>

        <multi-choice-answers
                v-if="type === 'one-answer'"
                ref="multi_choices"
                @updated="onMultiAnswersUpdate"
        ></multi-choice-answers>

        <single-answer
                v-if="type === 'free-text'"
                :default-value="freeTextAnswer"
                ref="single"
                @updated="onFreeTextUpdate"
        ></single-answer>

        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Reward Points:</label>
                </div>
                <b-select v-model="points" placeholder="Select some of points">
                    <option v-for="rewardPointType in rewardPointsTypes"
                            :value="rewardPointType">
                        {{rewardPointType}}
                    </option>
                </b-select>
            </div>
        </div>

        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Difficulty:</label>
                </div>
                <b-select v-model="difficulty" placeholder="Select the difficulty level">
                    <option v-for="difficultyType in difficultyTypes"
                            :value="difficultyType.code">
                        {{difficultyType.label}}
                    </option>
                </b-select>
            </div>

        </div>


        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Allowed attempts:</label>
                </div>
                <div class="field-body">
                    <b-field>
                        <b-radio v-model="isUnlimitedAttempts" :native-value="true" type="is-link">
                            <span>Unlimited:</span>
                        </b-radio>
                        <b-radio v-model="isUnlimitedAttempts" :native-value="false" type="is-link">
                            <span>Limited to:</span>
                        </b-radio>
                        <div class="field is-horizontal">
                            <b-input type="number" v-model="allowedAttempts"></b-input>
                        </div>
                        <div class="">
                            <label class="label" style="margin-left: 10px;">tries</label>
                        </div>
                    </b-field>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <div class="field is-horizontal">
                    <div class="field-label is-normal">
                        <label class="label" style="width: 180px"></label>
                    </div>
                    <div class="field-body">
                        <div class="field">
                            <div class="control">
                                <b-message type="is-warning">
                                    By default, players have to go to the challenge location to unlock it. By choosing
                                    “No lock”, you
                                    allow them to unlock it as soon as the game starts
                                </b-message>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label">Locking:</label>
                </div>
                <div class="field-body field control">
                    <b-field>
                        <b-radio v-model="unlocked_forced" :native-value="true" type="is-link">
                            <span>No lock</span>
                        </b-radio>
                        <b-radio v-model="unlocked_forced" :native-value="false" type="is-link">
                            <span>Geo-locked</span>
                        </b-radio>
                    </b-field>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <div class="field is-horizontal">
                    <div class="field-label is-normal">
                        <label class="label"></label>
                    </div>
                    <div class="field-body">
                        <div class="field">
                            <div class="control">
                                <b-message type="is-success">
                                    You can choose the challenge location with the map below. Move the pinpoint to where
                                    you want to put the challenge, or enter the coordinates yourself.
                                </b-message>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <geo-selector
                    :showZoom="false"
                    ref="geo_selector"
                    class="geo-selector"
                    :disable-auto-locate="disableAutoLocate"
                    @updated="onGeoSelectorUpdated"/>
        </div>

        <div class="field is-grouped" style="margin-top: 40px;">
            <div class="control">
                <button class="button is-light btn-margin" @click.prevent="goToPreviousPage()">Back to challenges list
                </button>
            </div>
            <div class="control">
                <button class="button is-link" @click.prevent="addQuestion" :disabled="isLoading" v-if="!isEdit">Create
                    this challenge
                </button>
                <button class="button is-link" @click.prevent="updateQuestion" :disabled="isLoading" v-if="isEdit">
                    Update this challenge
                </button>
            </div>
        </div>

    </form>
</template>

<script>
  import debounce from 'lodash.debounce'
  import markownIt from 'markdown-it'
  import emoji from 'markdown-it-emoji'
  import hljs from 'highlightjs'
  import PictureInput from 'vue-picture-input'
  import MultiChoiceAnswers from './single/MultiChoiceAnswers'
  import SingleAnswer from './single/SingleAnswer'
  import questionsService from '../services/questionsService'
  import BField from 'buefy/src/components/field/Field'
  import GeoSelector from './single/GeoSelector.vue'
  import imageHelper from '../helpers/image-helper'
  import displayHelper from '../helpers/display-helper'

  const markdown = markownIt({
    highlight: function (str, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return hljs.highlight(lang, str).value
        } catch (err) {
          console.log('Fail to highlight code snippet... Language:', lang)
        }
      }
      return ''
    }
  }).use(emoji)

  export default {
    name: 'question-form',
    components: {
      BField,
      MultiChoiceAnswers,
      SingleAnswer,
      PictureInput,
      GeoSelector,
    },
    created() {
      if (this.$route.params.id) {
        this.isEdit = true
        this.questionId = this.$route.params.id
        this.loadQuestionById(this.questionId)
        this.disableAutoLocate = true
      }
    },
    data() {
      return {
        // for update
        isEdit: false,
        questionLoaded: null,
        questionId: null,
        imagePreviewUrl: '',
        txtFilter: this.$route.params.txtFilter,

        // standard form
        errorMessage: '',
        createMode: false,
        name: '',
        category: null,
        isLoading: false,

        question: '',
        disableAutoLocate: false,

        fileUploadCustomTexts: {
          upload: '<h1>Bummer!</h1>',
          drag: 'Drag file here or click here to upload'
        },
        image: '',
        answers: [{
          value: '',
        }, {
          value: '',
        }],
        rewardPointsTypes: [
          100, 200, 300, 400, 500
        ],
        difficultyTypes: [
          {
            code: '1',
            label: 'Easy',
          },
          {
            code: '2',
            label: 'Medium',
          },
          {
            code: '3',
            label: 'Hard',
          }
        ],
        questionTypes: [
          {
            code: 'free-text',
            label: 'Free text',
            description: 'The player has to fill a blank field'
          },
          {
            code: 'one-answer',
            label: 'Multiple choices',
            description: 'The player has to choose the correct answer from a list',
          },
          {
            code: 'picture',
            label: 'Upload photo',
            description: 'The player has to upload a photo that answer the question (ex: take a photo of a physical object, a selfie doing a specific pose, ...)'
          }
        ],
        activityTypes: [
          {
            label: 'Quiz',
          },
          {
            label: 'Challenge',
          },
          {
            label: 'Finish line',
          },
          {
            label: 'Pictures',
          },
          {
            label: 'Voucher',
          },
          {
            label: 'Other',
          },
        ],
        type: 'one-answer',

        unlocked_forced: false,

        allowedAttempts: 1,
        difficulty: '1',
        activityType: 'Quiz',
        questionJson: '',

        points: 100,
        latitude: 0,
        longitude: 0,
        isUnlimitedAttempts: false,
        freeTextAnswer: '',
        correctAnswer: null
      }
    },
    computed: {
      compiledMarkdown: function () {
        return markdown.render(this.question)
      },
      typeDescription: function () {
        if (this.type) {
          for (const questionType of this.questionTypes) {
            if (questionType.code === this.type) {
              return questionType.description
            }
          }
        }
        return this.questionTypes[0].description
      },
    },
    watch: {
      'isUnlimitedAttempts': function (isUnlimitedAttempts) {
        if (isUnlimitedAttempts === true) {
          this.allowedAttempts = ''
        }
      },
      'type': function (type) {
        if (type === 'picture') {
          this.answers = [{
            value: '',
          }, {
            value: '',
          }]
          this.correctAnswer = ''
          this.freeTextAnswer = ''
        }
        if (type === 'free-text') {
          this.answers = [{
            value: '',
          }, {
            value: '',
          }]
          this.correctAnswer = ''
        } else {
          this.freeTextAnswer = ''
        }
      }
    },
    methods: {
      updateMarkdownPreview: debounce(function (e) {
        this.question = e.target.value
      }, 300),
      setCorrectAnswer() {
        if (this.type === 'free-text') {
          return this.freeTextAnswer
        } else {
          return this.correctAnswer + 1 + ''
        }
      },
      onMultiAnswersUpdate(payload) {
        this.answers = payload.answers
        this.correctAnswer = payload.correctAnswer
      },
      onFreeTextUpdate(freeTextAnswer) {
        this.freeTextAnswer = freeTextAnswer
      },
      updateQuestion() {
        this.isLoading = true
        const json = {
          challengeId: this.questionId,
          name: this.name,
          category: this.category,
          question: this.question,
          type: this.type,
          answers: this.transformAnswersForServer(this.answers),
          unlocked_forced: this.unlocked_forced,
          points: this.points,
          activityType: this.activityType,
          difficulty: parseInt(this.difficulty),
          location: {
            lat: parseFloat(this.latitude),
            lng: parseFloat(this.longitude)
          },
          allowedAttempts: this.allowedAttempts ? parseInt(this.allowedAttempts) : ''
        }

        json.correctAnswer = this.setCorrectAnswer()

        this.questionJson = json

        questionsService.updateQuestion(this.questionId, this.questionJson, this.image)
          .then(() => {
            this.goToPreviousPage()
          }).catch((error) => {
            displayHelper.displayError(this, 'Update challenge error: id=' + this.questionId + ' >>' + error.message)
          })
          .finally(() => {
            this.isLoading = false
          })
        this.createMode = false
      },
      addQuestion() {
        this.isLoading = true
        const json = {
          name: this.name,
          category: this.category,
          question: this.question,
          type: this.type,
          answers: this.transformAnswersForServer(this.answers),
          unlocked_forced: this.unlocked_forced,
          points: this.points,
          activityType: this.activityType,
          difficulty: parseInt(this.difficulty),
          location: {
            lat: parseFloat(this.latitude),
            lng: parseFloat(this.longitude)
          },
        }

        if (this.allowedAttempts) {
          json.allowedAttempts = parseInt(this.allowedAttempts)
        }
        json.correctAnswer = this.setCorrectAnswer()
        this.questionJson = json
        questionsService.addNewQuestion(this.questionJson, this.image)
          .then(() => {
            this.goToPreviousPage()
          }).catch((error) => {
            displayHelper.displayError(this, 'Create challenge error: ' + error.message)
          })
          .finally(() => {
            this.isLoading = false
          })
        this.createMode = false
      },
      onGeoSelectorUpdated(geo) {
        this.latitude = geo.lat
        this.longitude = geo.lng
      },
      transformAnswersForServer(answers) {
        const transformedAnswers = {}
        answers.forEach((answer, index) => {
          if (answer.value) {
            transformedAnswers[index + 1 + ''] = answer.value
          }
        })
        return transformedAnswers
      },
      onPictureSelected(image) {
        if (image) {
          imageHelper.compressImg(this.$refs.challengePicture.image)
            .then((compressedChallengePic) => {
              this.image = compressedChallengePic
            })
        }
      },
      hideImagePreview() {
        this.imagePreviewUrl = ''
      },
      goToPreviousPage() {
        this.$router.push({name: 'Questions', params: { txtFilter: this.txtFilter } })
      },
      loadQuestionById: function (questionId) {
        this.isLoading = true
        questionsService.getQuestionById(questionId)
          .then((question) => {
            console.log('Question received: ' + questionId)

            this.questionLoaded = question
            this.name = this.questionLoaded.name
            this.question = this.questionLoaded.question
            this.category = this.questionLoaded.category

            this.imagePreviewUrl = this.questionLoaded.imgSrcFullUrl

            this.type = this.questionLoaded.type

            if (this.questionLoaded.type === 'free-text') {
              this.freeTextAnswer = this.questionLoaded.correctAnswer
            } else {
              this.$refs.multi_choices.init(this.questionLoaded)
            }

            this.points = this.questionLoaded.points
            this.difficulty = this.questionLoaded.difficulty
            this.activityType = this.questionLoaded.activityType

            if (this.questionLoaded.allowedAttempts) {
              this.isUnlimitedAttempts = false
              this.allowedAttempts = this.questionLoaded.allowedAttempts
            } else {
              this.isUnlimitedAttempts = true
            }

            this.unlocked_forced = this.questionLoaded.unlocked_forced

            if (this.questionLoaded.location) {
              this.$refs.geo_selector.setLatLng(
                parseFloat(this.questionLoaded.location.lat),
                parseFloat(this.questionLoaded.location.lng)
              )
            }

            this.isLoading = false
          })
          .catch((err) => {
            displayHelper.displayError(this, 'Generic error: ' + err.message)
            this.isLoading = false
          })
      },
    },
  }
</script>

<style scoped>
    .game-form .is-horizontal .field-label {
        flex-grow: 0;
    }

    .game-form label {
        margin-bottom: 0;
        width: 180px !important;
    }

    .game-form .column .field {
        align-items: center;
    }

    .date-time /deep/ {
        z-index: 15 !important;
    }

    .picture-input /deep/ .preview-container {
        margin: 0;
        z-index: 1 !important;
    }

    .picture-input /deep/ .picture-preview {
        margin: 0;
        z-index: 1 !important;
    }

    .picture-input /deep/ .picture-inner {
        z-index: 1 !important;
    }

    .picture-input {
        text-align: left !important;
    }

    .picture-input /deep/ .preview-container {
        margin: 0;
    }

    #pictureInput, #injectedPreview {
        height: 150px;
        width: 250px;
        position: absolute;
    }

    #injectedPreviewImage {
        height: 150px;
        width: 250px;
    }

    #injectedPreview {
        top: 0;
        z-index: 10;
    }

    .field-label {
        flex-grow: 0;
    }

    .field-label label {
        width: 180px !important;
    }
</style>
